import { ComponentProps, HTMLProps, PropsWithChildren, ReactNode, use } from 'react';

import { MenuButton } from '~/modules/elements/akui/menu/menu-button';
import Box from '~/modules/elements/akui/primitives/box';
import Typography from '~/modules/elements/akui/primitives/typography';
import { dialogSurface } from '~/modules/elements/akui/surfaces';
import { ModalCloseButtonContext } from '~/modules/elements/modal-context';
import isE2E from '~/modules/utils/is-e2-e';
import { cn } from '~/utils/cn';
import { twx } from '~/utils/twx';

// Styling lives in the `menu-neighbour-glow` rules in index.css (needs a real CSS transition to
// fade in/out, which arbitrary Tailwind variants can't express cleanly). Skipped in e2e so it
// doesn't chase the same non-determinism the base focus styles avoid (see button.tsx).
const MenuContainer = twx(Box)(() => [
  'pointer-events-auto w-[100vw] items-stretch rounded-none [view-transition-name:menu-container] sm:max-w-[45rem] md:rounded-xl lg:max-w-[45rem] 2xl:max-w-[60rem]',
  !isE2E() && 'menu-neighbour-glow',
]);

export const MenuHelpText = twx(Typography)`text-md max-lg:text-xs`;
const MenuSubHeader = twx(Typography)`text-lg`;

interface MenuProps extends PropsWithChildren, Omit<HTMLProps<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  spacing?: 'regular' | 'tight';
  /** Build this menu from the shared `dialogSurface`, so it reads as part of the app rather than a
   * system dialog. Set on the top-level `Menu` a `Modal` wraps. */
  modal?: boolean;
}
export const Menu = ({ title, children, className, spacing = 'regular', modal = false, ref, ...props }: MenuProps) => (
  <MenuContainer
    {...props}
    className={`${modal ? `${dialogSurface} ` : ''}${className ?? ''} ${spacing === 'tight' ? 'gap-2 p-4' : 'gap-4 p-4 sm:p-6'}`}
    ref={ref}>
    {title && <MenuHeader>{title}</MenuHeader>}
    {children}
  </MenuContainer>
);

const MenuHeaderBase = twx(
  Typography,
)`text-active text-shadow-legible flex items-center justify-center text-xl font-bold uppercase max-lg:text-lg`;
/** In a modal filling a phone's screen it keeps clear of the close button in the corner */
export const MenuHeader = ({ className, ...props }: ComponentProps<typeof MenuHeaderBase>) => (
  <MenuHeaderBase
    className={cn(use(ModalCloseButtonContext) && 'phone:px-12 phone:text-center', className)}
    {...props}
  />
);
Menu.Header = MenuHeader;

Menu.Button = MenuButton;
Menu.ButtonGroup = twx.div`flex`;
Menu.HelpText = MenuHelpText;
Menu.SubHeader = MenuSubHeader;
/** `border-white/10` — the same edge the surface it divides is drawn with; a divider is not a
 * heavier thing than the panel around it. `/20` is reserved for the hover step. */
Menu.Divider = ({ className, ...props }: ComponentProps<'hr'>) => (
  <hr className={cn('border-white/10', className)} {...props} />
);
